import { KebabHorizontalIcon, PencilIcon, PlusIcon, SyncIcon, TrashIcon } from "@primer/octicons-react";
import { ActionList, ActionMenu, Box, useConfirm } from "@primer/react";
import { useCallback, useEffect, useState } from "react";
import { HOC_SINH_LICH_SU_Y_TE_TYPE_END_POINT, hocSinhLichSuYTeTypeApi } from "../../api/hocSinhLichSuYTeTypeApi";
import Button from "../../components/ui/button";
import DataTable from "../../components/ui/data-table";
import Text from "../../components/ui/text";
import { useCommonContext } from "../../contexts/common";
import { ts_hocsinh_lichsu_yte_type } from "../../models/response/hoc-sinh/ts_hocsinh_lichsu_yte_type";
import LichSuYTeTypeDetailModal from "./LichSuYTeTypeDetailModal";

const LichSuYTeTypePage = () => {
  const { translate, checkAccesiableTo } = useCommonContext();
  const confirm = useConfirm();

  const [lichSuYTeTypes, setLichSuYTeTypes] = useState<ts_hocsinh_lichsu_yte_type[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isShowModal, setIsShowModal] = useState(false);
  const [editingData, setEditingData] = useState<ts_hocsinh_lichsu_yte_type | undefined>(undefined);

  // Phân quyền API
  // const canView = checkAccesiableTo(HOC_SINH_LICH_SU_Y_TE_TYPE_END_POINT, "GET");
  // const canInsert = checkAccesiableTo(HOC_SINH_LICH_SU_Y_TE_TYPE_END_POINT, "POST");
  // const canUpdate = checkAccesiableTo(HOC_SINH_LICH_SU_Y_TE_TYPE_END_POINT, "PUT");
  // const canDelete = checkAccesiableTo(`${HOC_SINH_LICH_SU_Y_TE_TYPE_END_POINT}/{id}`, "DELETE");

  const canView = true;
  const canInsert = true;
  const canUpdate = true;
  const canDelete = true;
  
  const handleReload = useCallback(async () => {
    if (!canView) return;

    setIsLoading(true);
    try {
      const response = await hocSinhLichSuYTeTypeApi.selectAll();

      if (response.is_success) {
        setLichSuYTeTypes(response.data || []);
      }
    } catch (error) {
      console.error("Error loading lich su y te types:", error);
    } finally {
      setIsLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    if (!canView) return;
    handleReload();
  }, [canView, handleReload]);

  const handleDelete = async (id: number) => {
    if (!canDelete) return;

    if (await confirm({
      content: translate("Base.Label.DeleteConfirm.Content"),
      title: translate("Base.Label.Confirm"),
      cancelButtonContent: translate("Base.Label.Close"),
      confirmButtonContent: translate("Base.Label.Delete"),
      confirmButtonType: "danger"
    })) {
      try {
        const response = await hocSinhLichSuYTeTypeApi.delete(id);
        if (response.is_success) {
          handleReload();
        }
      } catch (error) {
        console.error("Error deleting lich su y te type:", error);
      }
    }
  };

  // Kiểm tra quyền truy cập
  if (!canView) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
        <Text text="Bạn không có quyền xem thông tin loại lịch sử y tế" />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <DataTable
        data={lichSuYTeTypes}
        isLoading={isLoading}
        title="Loại lịch sử y tế"
        subTitle={`${translate("Base.Label.TotalCount")}: ${lichSuYTeTypes.length}`}
        searchEnable
        height="calc(100vh - 260px)"
        paging={{
          enable: true,
          pageSizeItems: [20, 50, 200, 500, 1000],
        }}
        actionComponent={
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {canInsert && (
              <Button text="Base.Label.AddNew" leadingVisual={PlusIcon}
                size="medium"
                variant="primary"
                onClick={() => {
                  setEditingData(undefined);
                  setIsShowModal(true);
                }}
              />
            )}
            {canView && (
              <Button text="Base.Label.Refresh" leadingVisual={SyncIcon}
                size="medium"
                onClick={() => {
                  handleReload();
                }}
              />
            )}
          </Box>
        }
        columns={[
          {
            id: "cmd",
            caption: "#",
            width: "50px",
            align: "center",
            cellRender: (data: ts_hocsinh_lichsu_yte_type) => {
              const hasEditPermission = canUpdate;
              const hasDeletePermission = canDelete;

              // Hiển thị box trống nếu không có quyền nào
              if (!hasEditPermission && !hasDeletePermission) {
                return <Box sx={{ m: -1 }}></Box>;
              }

              return (
                <ActionMenu>
                  <ActionMenu.Button icon={KebabHorizontalIcon} variant="invisible" sx={{ m: -1 }}>&nbsp;</ActionMenu.Button>
                  <ActionMenu.Overlay width="auto">
                    <ActionList>
                      {hasEditPermission && (
                        <ActionList.Item onSelect={() => {
                          setEditingData(data);
                          setIsShowModal(true);
                        }}>
                          <ActionList.LeadingVisual><PencilIcon /></ActionList.LeadingVisual>
                          <Text text='Base.Label.Edit' />
                        </ActionList.Item>
                      )}
                      {hasDeletePermission && (
                        <>
                          {hasEditPermission && <ActionList.Divider />}
                          <ActionList.Item variant="danger" onSelect={() => {
                            handleDelete(data.id)
                          }}>
                            <ActionList.LeadingVisual><TrashIcon /></ActionList.LeadingVisual>
                            <Text text='Base.Label.Delete' />
                          </ActionList.Item>
                        </>
                      )}
                    </ActionList>
                  </ActionMenu.Overlay>
                </ActionMenu>
              );
            }
          },
          {
            dataField: "nhom_noi_dung",
            caption: "Nhóm nội dung",
          },
          {
            dataField: "nhom_noi_dung_en",
            caption: "Nhóm nội dung (Tiếng Anh)",
          },
        ]}
      />
      {isShowModal && (canInsert || canUpdate) && (
        <LichSuYTeTypeDetailModal
          editingData={editingData}
          onClose={() => {
            setIsShowModal(false);
          }}
          onSuccess={() => {
            handleReload();
            setIsShowModal(false);
          }}
        />
      )}
    </Box>
  );
};

export default LichSuYTeTypePage;
